import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, switchMap } from 'rxjs/operators';
import { from, of } from 'rxjs';
import {
  BaseOneResponseInterface,
  BulkResponseDataInterface,
} from '../../../../shared/model/interface/crud-response-interface.model';
import * as oeeAppReducer from '../../../oee.reducer';
import * as AppActions from '../../../app/actions';
import { ErrorMessageService } from '../../../../shared/service/error-message.service';
import { ILineWatchdogSetting } from '../../../../view/settings/api-automation-settings/line-watchdog-settings/line-watchdog-settings.model';
import { LineWatchdogSettingService } from './line-watchdog-settings.service';
import {
  ActionTypes,
  LineWatchdogSettingsLoading,
  LineWatchdogSettingsLoaded,
  FetchError,
  GetLineWatchdogSetting,
  GetLineWatchdogSettingCompleted,
  EditLineWatchdogSetting,
  EditLineWatchdogSettingCompleted,
  AddLineWatchdogSetting,
  AddLineWatchdogSettingCompleted,
  DeleteLineWatchdogSettings,
  DeleteLineWatchdogSettingsCompleted,
  SiteDataLoading,
  SiteDataCompleted,
  LineDataLoading,
  LineDataCompleted,
  ActivitiesDataLoading,
  ActivitiesDataCompleted,
} from './line-watchdog-settings.actions';
import { LineService } from '../../../../shared/service/line/line.service';
import { SiteService } from '../../../../shared/service/filter/site.service';
import { ActivityService } from '../../../../shared/service/filter/activity.service';

@Injectable()
export class LineWatchdogSettingEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly service: LineWatchdogSettingService,
    private readonly store: Store<oeeAppReducer.OeeAppState>,
    private readonly errorMessageService: ErrorMessageService,
    private readonly siteService: SiteService,
    private readonly lineService: LineService,
    private readonly activityService: ActivityService,
  ) {}

  getDataForDatatable = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.LINE_WATCHDOG_SETTINGS_DATA_LOADING),
      switchMap((payload: LineWatchdogSettingsLoading) => {
        const andConditions = [];
        const searchText: string = payload.params.searchText?.trim() ?? '';
        let httpParams: HttpParams = new HttpParams()
          .append('sort', 'id,DESC')
          .append('limit', String(payload.params.perPage || 1000))
          .append('page', String(payload.params.page));

        if (payload.params.siteIds?.length) {
          andConditions.push({ siteId: { $in: payload.params.siteIds } });
        }

        if (payload.params.lineIds?.length) {
          andConditions.push({ lineId: { $in: payload.params.lineIds } });
        }

        if (searchText) {
          andConditions.push({
            $or: [{ 'site.name': { $cont: searchText } }, { 'line.title': { $cont: searchText } }],
          });
        }

        if (andConditions.length > 0) {
          httpParams = httpParams.set('s', JSON.stringify({ $and: andConditions }));
        }

        if (payload.params.orderBy && payload.params.orderDesc !== undefined) {
          httpParams = httpParams.set('sort', `${payload.params.orderBy},${payload.params.orderDesc}`);
        }

        return this.service.getData(httpParams).pipe(
          switchMap((response) => of(new LineWatchdogSettingsLoaded(response))),
          catchError((error) => of(new FetchError(error))),
        );
      }),
      catchError((error) => of(new FetchError(error))),
    ),
  );

  getOne = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.GET_LINE_WATCHDOG_SETTING),
      switchMap((objectData: GetLineWatchdogSetting) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.getOne(objectData.recordId).pipe(
          switchMap((response: BaseOneResponseInterface<ILineWatchdogSetting>) =>
            of(new GetLineWatchdogSettingCompleted(response), new AppActions.HideLoader()),
          ),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  addOne = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.ADD_LINE_WATCHDOG_SETTING),
      switchMap((objectData: AddLineWatchdogSetting) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.addOne(objectData.record).pipe(
          switchMap((response: BaseOneResponseInterface<ILineWatchdogSetting>) =>
            of(
              new AddLineWatchdogSettingCompleted(response),
              ...(objectData.hideLoaderAfterSuccess ? [new AppActions.HideLoader()] : []),
            ),
          ),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  editOne = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.EDIT_LINE_WATCHDOG_SETTING),
      switchMap((objectData: EditLineWatchdogSetting) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.editOne(objectData.recordId, objectData.record).pipe(
          switchMap((response: BaseOneResponseInterface<ILineWatchdogSetting>) =>
            of(
              new EditLineWatchdogSettingCompleted(response),
              ...(objectData.hideLoaderAfterSuccess ? [new AppActions.HideLoader()] : []),
            ),
          ),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  deleteOnes = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.DELETE_LINE_WATCHDOG_SETTINGS),
      switchMap((objectData: DeleteLineWatchdogSettings) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.deleteOnes(objectData.payload).pipe(
          switchMap((response: BulkResponseDataInterface) => {
            this.errorMessageService.getTranslatedErrorMessage(response.data);

            return of(
              new DeleteLineWatchdogSettingsCompleted(objectData.payload.length > 1, response),
              new AppActions.HideLoader(),
            );
          }),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  getSiteData = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.SITES_DATA_LOADING),
      switchMap((objectData: SiteDataLoading) =>
        from(this.siteService.getData(new HttpParams().set('limit', '1000'))).pipe(
          switchMap((response) => of(new SiteDataCompleted(response))),
          catchError((error) => of(new FetchError(error))),
        ),
      ),
      catchError((error) => of(new FetchError(error))),
    ),
  );

  getLineData = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.LINES_DATA_LOADING),
      switchMap((objectData: LineDataLoading) =>
        this.lineService
          .getLines(
            new HttpParams()
              .set('s', JSON.stringify({ statusId: 1, siteId: objectData.siteId }))
              .set('limit', '1000')
              .set('join', 'lineTypeName'),
            true,
          )
          .pipe(
            switchMap((response) => of(new LineDataCompleted(response))),
            catchError((error) => of(new FetchError(error))),
          ),
      ),
      catchError((error) => of(new FetchError(error))),
    ),
  );

  getActivitiesData = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.ACTIVITIES_DATA_LOADING),
      switchMap((objectData: ActivitiesDataLoading) =>
        from(
          this.activityService.getData(new HttpParams().set('s', JSON.stringify({ active: 1 })).set('limit', '1000')),
        ).pipe(
          switchMap((response) => of(new ActivitiesDataCompleted(response))),
          catchError((error) => of(new FetchError(error))),
        ),
      ),
      catchError((error) => of(new FetchError(error))),
    ),
  );
}
